


























































































































































import * as _ from 'lodash';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { blocks, duration, durationClass, rows, factor } from '@/filters';
import { EstimateDirection, CenterMode, BufferLocation, HighlightMode, NodeProp, Metric } from '../enums';
import Node from '@/inode';
import { IPlan } from '../iplan';

@Component({
  filters: {
    durationClass,
  },
})
export default class Diagram extends Vue {
  @Prop() private plan!: IPlan;
  @Prop() private eventBus!: InstanceType<typeof Vue>;

  // The main plan + init plans (all flatten)
  private plans: any[][] = [[]];
  private lodash = _;
  private nodeProps = NodeProp;
  private metrics = Metric;
  private bufferLocations = BufferLocation;
  private estimateDirections = EstimateDirection;
  private centerModes = CenterMode;
  private highlightModes = HighlightMode;
  private highlightedNode: Node | null = null;

  private viewOptions: any = {
    metric: Metric.time,
    buffersMetric: BufferLocation.shared,
  };

  private created(): void {
    const savedOptions = localStorage.getItem('diagramViewOptions');
    if (savedOptions) {
      _.assignIn(this.viewOptions, JSON.parse(savedOptions));
    }
    this.flatten(this.plans[0], 0, this.plan.content.Plan, true, []);

    _.each(this.plan.ctes, (cte) => {
      const flat = [] as any[];
      this.flatten(flat, 0, cte, true, []);
      this.plans.push(flat);
    });
    this.eventBus.$on('mouseovernode', (node: Node) => { this.highlightedNode = node; });
    this.eventBus.$on('mouseoutnode', () => { this.highlightedNode = null; });

    // switch to the first buffers tab if data not available for the currently
    // chosen one
    const planBufferLocations = _.keys(this.plan.planStats.maxBlocks);
    if (_.indexOf(planBufferLocations, this.viewOptions.buffersMetric) === -1) {
      this.viewOptions.buffersMetric = _.min(planBufferLocations);
    }
  }

  @Watch('viewOptions', {deep: true})
  private onViewOptionsChanged(val: any, oldVal: any) {
    localStorage.setItem('diagramViewOptions', JSON.stringify(this.viewOptions));
  }

  private tooltip(node: Node): string {
    let content = '';
    switch (this.viewOptions.metric) {
      case Metric.time:
        content += this.timeTooltip(node);
        break;
      case Metric.rows:
        content += this.rowsTooltip(node);
        break;
      case Metric.estimate_factor:
        content += this.estimateFactorTooltip(node);
        break;
      case Metric.cost:
        content += this.costTooltip(node);
        break;
      case Metric.buffers:
        content += this.buffersTooltip(node);
        break;
    }
    if (node[NodeProp.CTE_NAME]) {
      content +=  '<br><em>CTE ' + node[NodeProp.CTE_NAME] + '</em>';
    }
    return content;
  }

  private timeTooltip(node: Node): string {
    return [
      'Duration: <br>Exclusive: ',
      duration(node[NodeProp.EXCLUSIVE_DURATION], true),
      ', Total: ',
      duration(node[NodeProp.ACTUAL_TOTAL_TIME], true),
    ].join('');
  }

  private rowsTooltip(node: Node): string {
    return [
      'Rows: ',
      rows(node[NodeProp.ACTUAL_ROWS]),
    ].join('');
  }

  private estimateFactorTooltip(node: Node): string {
    const estimateFactor = node[NodeProp.PLANNER_ESTIMATE_FACTOR];
    const estimateDirection = node[NodeProp.PLANNER_ESTIMATE_DIRECTION];
    let text = '';
    switch (estimateDirection) {
      case EstimateDirection.over:
        text += '<i class="fa fa-arrow-up"></i> over';
        break;
      case EstimateDirection.under:
        text += '<i class="fa fa-arrow-down"></i> under';
        break;
      default:
        text += 'Correctly';
    }
    text += ' estimated';
    text += estimateFactor ? ' by <b>' + factor(estimateFactor) + '</b>' : '';
    return text;
  }

  private costTooltip(node: Node): string {
    return [
      'Cost: ',
      rows(node[NodeProp.EXCLUSIVE_COST]),
    ].join('');
  }

  private buffersTooltip(node: Node): string {
    let text = '';
    let hit;
    let read;
    let written;
    let dirtied;
    switch (this.viewOptions.buffersMetric) {
      case BufferLocation.shared:
        hit = node[NodeProp.EXCLUSIVE_SHARED_HIT_BLOCKS];
        read = node[NodeProp.EXCLUSIVE_SHARED_READ_BLOCKS];
        dirtied = node[NodeProp.EXCLUSIVE_SHARED_DIRTIED_BLOCKS];
        written = node[NodeProp.EXCLUSIVE_SHARED_WRITTEN_BLOCKS];
        break;
      case BufferLocation.temp:
        read = node[NodeProp.EXCLUSIVE_TEMP_READ_BLOCKS];
        written = node[NodeProp.EXCLUSIVE_TEMP_WRITTEN_BLOCKS];
        break;
      case BufferLocation.local:
        hit = node[NodeProp.EXCLUSIVE_LOCAL_HIT_BLOCKS];
        read = node[NodeProp.EXCLUSIVE_LOCAL_READ_BLOCKS];
        dirtied = node[NodeProp.EXCLUSIVE_LOCAL_DIRTIED_BLOCKS];
        written = node[NodeProp.EXCLUSIVE_LOCAL_WRITTEN_BLOCKS];
        break;
    }
    text += '<table class="table text-white table-sm table-borderless mb-0">';
    text += hit ? '<tr><td>Hit:</td><td class="text-right">' + blocks(hit) + '</td></tr>' : '';
    text += read ? '<tr><td>Read:</td><td class="text-right">' + blocks(read) + '</td></tr>' : '';
    text += dirtied ? '<tr><td>Dirtied:</td><td class="text-right">' + blocks(dirtied) + '</td></tr>' : '';
    text += written ? '<tr><td>Written:</td><td class="text-right">' + blocks(written) + '</td></tr>' : '';
    text += '</table>';

    if (!hit && !read && !dirtied && ! written) {
      text = ' N/A';
    }

    switch (this.viewOptions.buffersMetric) {
      case BufferLocation.shared:
        text = 'Shared Blocks:' + text;
        break;
      case BufferLocation.temp:
        text = 'Temp Blocks:' + text;
        break;
      case BufferLocation.local:
        text = 'Local Blocks:' + text;
        break;
    }
    return text;
  }

  private nodeType(row: any[]): string {
    return row[1][NodeProp.NODE_TYPE];
  }

  private flatten(output: any[], level: number, node: any, isLast: boolean,
                  branches: number[]) {
    // [level, node, isLastSibbling, branches]
    output.push([level, node, isLast, _.concat([], branches)]);
    if (!isLast) {
      branches.push(level);
    }

    _.each(node.Plans, (subnode) => {
      this.flatten(
        output,
        level + 1,
        subnode,
        subnode === _.last(node.Plans),
        branches,
      );
    });
    if (!isLast) {
      branches.pop();
    }
  }

  private get maxEstimateFactor(): number {
    const max = _.max(_.map(this.plans, (plan) => {
      return _.max(_.map(plan, (row) => {
        return row[1][NodeProp.PLANNER_ESTIMATE_FACTOR];
      }));
    }));
    return max * 2 || 1;
  }

  private get dataAvailable(): boolean {
    if (this.viewOptions.metric === this.metrics.buffers) {
      // if current metrics is buffers, view options for buffers should be
      // undefined if there's no buffer data
      return this.viewOptions.buffersMetric;
    }
    return true;
  }
}
